import { useMemo, type RefObject } from 'react';
import type { RTableHandle } from '../RTable.props';

/** DOM-геометрия остаётся внутри RTable; ID совпадает с ключом строки TanStack. */
export function useRowPosition(viewportRef: RefObject<HTMLDivElement | null>): RTableHandle {
  return useMemo(() => {
    const getRows = (viewport: HTMLElement) => {
      const root = viewport.closest('[data-rtable-root]');
      // renderSubComponent может содержать другую RTable: её строки принадлежат ей.
      return Array.from(viewport.querySelectorAll<HTMLElement>('[data-rtable-row-id]')).filter(
        row => row.closest('[data-rtable-root]') === root
      );
    };

    const getViewportBounds = () => {
      const viewport = viewportRef.current;
      if (!viewport || viewport.clientHeight === 0) return null;
      const header = viewport.querySelector<HTMLElement>('[data-rtable-header]');
      const top = viewport.getBoundingClientRect().top + viewport.clientTop;
      const bottom = top + viewport.clientHeight;
      const contentTop = top + (header?.getBoundingClientRect().height ?? 0);
      return { viewport, top: contentTop, bottom, center: (contentTop + bottom) / 2 };
    };

    return {
      getCenteredRowId() {
        const bounds = getViewportBounds();
        if (!bounds) return null;
        let centeredRowId: string | null = null;
        let nearestDistance = Infinity;
        for (const row of getRows(bounds.viewport)) {
          const rect = row.getBoundingClientRect();
          if (rect.bottom <= bounds.top || rect.top >= bounds.bottom) continue;
          const distance = Math.abs((rect.top + rect.bottom) / 2 - bounds.center);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            centeredRowId = row.dataset.rtableRowId ?? null;
          }
        }
        return centeredRowId;
      },
      scrollToRow(rowId) {
        const bounds = getViewportBounds();
        if (!bounds) return false;
        // Сравниваем ID как строку, без интерполяции в CSS-селектор.
        const row = getRows(bounds.viewport).find(element => element.dataset.rtableRowId === rowId);
        if (!row) return false;
        const rect = row.getBoundingClientRect();
        bounds.viewport.scrollTop += (rect.top + rect.bottom) / 2 - bounds.center;
        return true;
      },
      scrollToTop() {
        if (viewportRef.current) viewportRef.current.scrollTop = 0;
      },
    };
  }, [viewportRef]);
}
